<template>
  <mdb-container>
    <div v-if="currentGroup">
      <banner-small :group="currentGroup" title="Venues"/>
    </div>

    <div class="text-left" v-show="!currentVenue" v-if="currentGroup">
      <mdb-btn @click="createVenue" size="sm">
        <mdb-icon icon="plus"/>
        New Venue
      </mdb-btn>

      <data-table :clickHandler="editVenue"
                  :columns="tableData.columns"
                  :deleted="deleted"
                  :dataSource="'/venue/listAll/' + currentGroup.vanityPath"
      />
    </div>
    <div class="text-left" v-if="currentVenue">
      <mdb-btn @click="navTop" size="sm">
        <mdb-icon icon="arrow-left"/>
        Venue List
      </mdb-btn>
      <span v-if="!currentVenue.isDefault && currentVenue.id && currentGroup.role=='Admin'">
        <mdb-btn @click="setDefault" size="sm">Set as Default Venue</mdb-btn>
        <mdb-tooltip :options="{placement: 'right'}" trigger="hover">
          <span slot="tip">If set as default, new events will default to this venue.
          </span>
          <mdb-icon icon="info-circle" slot="reference"/>
        </mdb-tooltip>
      </span>
      <span v-if="currentVenue.isDefault">
        <b><i>This is the default venue for new events.</i></b>
      </span>
      <form @keypress.13.prevent @submit.prevent="save" class="text-left needs-validation">
        <mdb-row>
          <mdb-col col="6">
            <mdb-input id="venueName" label="Name" placeholder="Name"
                       required type="text"
                       v-model="currentVenue.name"
            />
          </mdb-col>
          <mdb-col col="6">
            <div class="form-check">
              <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                <span slot="tip">When disabled, you may not choose the venue when creating events.
                </span>
                <mdb-icon icon="info-circle" slot="reference"/>
              </mdb-tooltip>
              <input aria-label="Disabled?" class="form-control form-check-input" id="disabled" type="checkbox"
                     v-model="currentVenue.disabled"/>
              <label for="disabled">Disabled</label>
            </div>
            <div class="form-check">
              <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                <span slot="tip">Often it's nice to see images from other events that happened at a given
                  venue. However, some venues are meeting points (such as trail heads, boat launches, etc.) so
                  the images taken at events don't necessarily reflect the venue. Check this box if you don't want
                  images shown on venue information pages.
                </span>
                <mdb-icon icon="info-circle" slot="reference"/>
              </mdb-tooltip>
              <input aria-label="Hide Gallery?" class="form-control form-check-input" id="hideGallery"
                     type="checkbox" v-model="currentVenue.hideGallery"/>
              <label for="hideGallery">Hide Gallery</label>
            </div>
          </mdb-col>
        </mdb-row>
        <mdb-row v-if="currentVenue.events == 0 && !currentVenue.isDefault">
          <mdb-col col="12">
            <div class="text-info">This venue is not used by any events.
              <mdb-btn @click="showYesNo=true" color="danger" rounded size="sm">Delete</mdb-btn>
            </div>
          </mdb-col>
        </mdb-row>
        <mdb-row v-if="currentVenue.events > 0">
          <mdb-col col="12">
            <div class="text-info">This venue has been used by {{ currentVenue.events }}
              {{ currentVenue.events == 1 ? 'event' : 'events' }} and cannot be deleted. You may disable it
              to prevent it from being used on future events.
              <mdb-btn @click="getEvents" icon="list" rounded size="sm">View Events</mdb-btn>
            </div>
          </mdb-col>
        </mdb-row>
        <mdb-row>
          <mdb-col col="6">
            <mdb-input @blur="addressHelp=false" @focus="addressHelp=true"
                       label="Address/GPS Coordinates" placeholder="Address or GPS Coordinates" type="text"
                       v-model="currentVenue.address"
            />
            <div class="form-text" v-if="addressHelp">
              <mdb-icon icon="info-circle"/>
              Use a full address or GPS coordinates that work with Google Maps. We will
              use this information to generate a map for your events using this venue.
            </div>

            <mdb-btn :href="'https://maps.google.com?q='+currentVenue.address" icon="external-link-alt" rounded
                     size="sm" tag="a" target="_blank"
                     type=""
                     v-if="currentVenue.address"> View on Google Maps
            </mdb-btn>
          </mdb-col>
          <mdb-col col="6">
            <mdb-input @blur="urlHelp=false" @focus="urlHelp=true"
                       label="Location URL" placeholder="URL of venue" type="text" v-model="currentVenue.locationURL"
            />
            <div class="form-text color-primary" v-if="urlHelp">
              <mdb-icon icon="info-circle"/>
              If the venue has its own web site or there's a site with additional
              information about this venue, put that URL here.
            </div>
            <mdb-btn :href="currentVenue.locationURL" icon="external-link-alt" rounded
                     size="sm" tag="a" target="_blank"
                     type=""
                     v-if="currentVenue.locationURL"> Test URL
            </mdb-btn>
          </mdb-col>
        </mdb-row>
        <hr>
        <mdb-row>
          <mdb-col col="12">
            <div class="formless-label">Venue Details</div>
            <div id="text-wizard-area" class="grey lighten-5 border">
              <editor :init="editorConfig" v-model="currentVenue.descr"/>
            </div>
          </mdb-col>
        </mdb-row>
        <mdb-btn type="submit">
          <mdb-icon icon="save"/>
          Save
        </mdb-btn>
      </form>
      <mdb-modal :show="showImageSelector" @close="showImageSelector = false">
        <mdb-modal-header :close="true" center>Select an Image
        </mdb-modal-header>
        <mdb-modal-body>
          <image-selector :group="currentGroup" @imageSelected="imageSelected" emitOnSelection="imageSelected"/>
        </mdb-modal-body>
      </mdb-modal>
      <mdb-modal @close="showEventList = false" v-if="showEventList">
        <mdb-modal-header :close="true" center class="secondary-color text-white">{{ currentVenue.name }} Event List
        </mdb-modal-header>
        <mdb-modal-body>
          <mdb-tbl class="text-left" responsive striped>
            <mdb-tbl-head class="primary-color text-white">
              <tr>
                <th>Event</th>
                <th>Date</th>
              </tr>
            </mdb-tbl-head>
            <mdb-tbl-body>
              <tr v-for="event in events" :id="event.id">
                <td>
                  <router-link :to="'/event/show/' + event.id">
                    {{event.title}}</router-link>
                </td>
                <td>{{event.date}}</td>
              </tr>
            </mdb-tbl-body>
          </mdb-tbl>

        </mdb-modal-body>
      </mdb-modal>

    </div>
    <yes-no-modal @no="showYesNo=false" @yes="deleteVenue"
                  additional-text="This cannot be undone."
                  question="Are you sure you wish to delete this venue?" v-if="showYesNo"/>

  </mdb-container>

</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import {mapState} from 'vuex'
import bannerSmall from '../group/BannerSmall'
import dataTable from '../common/DataTable'
import editor from '@tinymce/tinymce-vue'
import YesNoModal from "../common/YesNoModal";
import editorSettings from "../../services/editorSettings";
import {
  mdbBtn,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbTbl,
  mdbTblBody,
  mdbTblHead,
  mdbTooltip
} from 'mdbvue';
import imageSelector from '../group/ImageSelector'

export default {
  name: "group-venues",
  display: "venue",
  order: 1,
  components: {
    YesNoModal,
    mdbContainer,
    mdbBtn,
    mdbRow,
    mdbCol,
    mdbIcon,
    mdbInput,
    mdbTooltip,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    bannerSmall,
    dataTable,
    editor,
    imageSelector
  },
  computed: mapState({
                       currentGroup: state => state.currentGroup
                     }),
  data() {
    return {
      currentVenue: null,
      tableData: {
        columns: [{field: "name", label: "Venue"},
          {field: "events", label: "# Events", type: "number"},
          {field: "disabled", label: "Disabled?", type: "checkbox"},
          {field: "id", name: "ID", hidden: true}], rows: []
      },  //may be filtered
      currentView: null,
      haveTableData: false,
      query: "",
      editing: null,
      isTyping: false,
      addressHelp: false,
      urlHelp: false,
      showImageSelector: false,
      imageCallback: null,
      showYesNo: false,
      showEventList: false,
      events: [],
      editorConfig: editorSettings.theWorksInline(),
      deleted: []

    }
  },
  async mounted() {
    document.title = "Venues"
    this.editorConfig.file_picker_callback = this.myFileBrowser
    await this.$store.dispatch("setExecuting", true)
    await api.groupBasics(this.$route.params.id, "organizer")
    if (!this.currentGroup) {
      await this.$store.dispatch("setExecuting", false)
      this.$notify.error({message: "Could not access group " + this.$route.params.id, timeOut: 10000})
      return
    }
    await this.$store.dispatch("setExecuting", false)
    if (this.$route.params.venueId) {
      let row = {id: this.$route.params.venueId}
      this.editVenue(row)
    }
  },
  methods: {
    navTop: function () {
      this.currentVenue = null
      if (this.$route.params.venueId) {
        this.$router.replace("/group/" + this.currentGroup.vanityPath + "/venue");
      }
    },
    save: function () {
      this.$store.dispatch("setExecuting", true)
      if (this.currentVenue.id) {
        api.post("/venue/save/" + this.currentVenue.id, this.currentVenue).then(
          (details) => {
            this.$notify.success({message: "Venue updated successfully.", timeOut: 3000})
          },
          (error) => {
            this.$notify.error({message: error.response.data})
          }
        ).then(() => {
          this.$store.dispatch("setExecuting", false)
        })
      } else {
        api.put("/venue/create/" + this.currentGroup.vanityPath, this.currentVenue).then(
          (details) => {
            this.$notify.success({message: "Venue created successfully.", timeOut: 3000})
            this.currentVenue.id = details.data.id
          },
          (error) => {
            this.$notify.error({message: error.response.data, timeOut: 3000})
          }
        ).then(() => {
          this.$store.dispatch("setExecuting", false)
        })
      }
    },
    setDefault: function () {
      this.$store.dispatch("setExecuting", true)
      api.post("/venue/setDefault/" + this.currentVenue.id, {}).then(
        (details) => {
          this.currentVenue.isDefault = true
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }
      ).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    createVenue: function () {
      this.currentVenue = {
        id: null,
        name: "",
        hideGallery: false,
        disabled: false,
        address: "",
        locationURL: "",
        descr: "",
        isDefault: false
      }
    },
    editVenue: function (row) {
      this.$store.dispatch("setExecuting", true)
      this.editing = row
      api.get("/venue/show/" + row.id).then(
        (details) => {
          this.currentVenue = details.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 7000})
        }
      ).then(() => {
        this.$store.dispatch("setExecuting", false)
        let el = document.getElementById("venueName")
        if (el) el.focus()
      })
    },
    deleteVenue: function () {
      this.showYesNo = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/venue/delete/" + this.currentVenue.id).then(
        (details) => {
          this.deleted.push(this.currentVenue.id)
          this.currentVenue = null
          this.navTop()
          this.$notify.success({message: "Venue deleted.", timeOut: 2000})
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 7000})
        }
      ).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    imageSelected: function (image) {
      this.showImageSelector = false
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:block")
      this.imageCallback(image.url, {width: "33%", height: ""})
    },
    myFileBrowser: function (callBack, value, meta) {
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:none")
      this.showImageSelector = true
      this.imageCallback = callBack
    },
    getEvents: function () {
      this.$store.dispatch("setExecuting", true)
      api.get("/venue/eventList/" + this.currentVenue.id).then(
        (details) => {
          this.events = details.data
          this.showEventList = true
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 7000})
        }
      ).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
  }
};

</script>
<style scoped>
.no-padding {
  padding: 0 !important;
}

</style>


